/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
  Box,
  Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  getTotalBudgetString,
  getPPPDString,
} from '@marageti/z4-lib';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { titleCase } from '../../../../utils/helpers';
import { formatCountryCodesToString } from '../../utils';
import { TravelerChips, TripMatchStatusChip } from '../../chips';
import { PhoneIndicator, ChatIndicator, SalesStageIndicator } from '../../trip-indicators';

const {
  OutlinedUserGroupIcon,
  OutlinedLocationMarkerIcon,
  OutlinedCurrencyDollarIcon,
  OutlinedUserIcon,
  OutlinedInformationCircleIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);

const showToolTip = (tripMatch: TripMatch) => [
  TripMatchStatus.MatchSold,
  TripMatchStatus.MatchSoldCanceled,
  TripMatchStatus.MatchRefundedCanceled,
].includes(tripMatch.status);

type AllCardViewProps = {
  countryCodes: CountryCodeMap | undefined;
  tripMatch: TripMatch;
  showAssignedSpecialist?: boolean;
};

const AllCardView = ({
  countryCodes,
  tripMatch,
  showAssignedSpecialist = false,
}: AllCardViewProps) => {
  const {
    travelers,
    countries,
    departureDate: tmDepartureDate,
    totalNights: tmTotalNights,
    numberOfAdults,
    numberOfChildren,
    budget,
    partnerName,
    createdAt,
    pppd,
    totalTripPrice,
    assignedToName,
  } = tripMatch;
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const fullName = titleCase(travelers[0].fullName);
  const countryNames = countryCodes ? formatCountryCodesToString(countries, countryCodes) : '';
  const departureDate = getFormattedDateShortMonthDDYYYY(tmDepartureDate) || '';
  const totalNights = `${tmTotalNights} Nights`;
  const numberOfTravelers = numberOfAdults + numberOfChildren;
  const totalTravelers = numberOfTravelers === 1
    ? '1 Traveler'
    : `${numberOfTravelers} Travelers`;
  const matchedDate = getFormattedDateShortMonthDDYYYY(createdAt) || '';

  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={`/trip-matches/${tripMatch?.id}`}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography variant="bodyBold">
                {fullName}
              </Typography>
              <TravelerChips
                travelers={travelers}
                referralType={tripMatch.referralType}
                repeatTraveler={tripMatch.repeatTraveler}
              />
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedUserGroupIcon} label={totalTravelers} />
              <IconLabel icon={OutlinedLocationMarkerIcon} label={countryNames} />
              <IconLabel
                icon={DepartureIcon}
                label={(
                  <>
                    {departureDate}
                    {' '}
                    ·
                    {' '}
                    {totalNights}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedCurrencyDollarIcon}
                label={(
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {getTotalBudgetString(budget)}
                    {' '}
                    Total Budget
                    {' '}
                    ·
                    {' '}
                    {getPPPDString(pppd)}
                    {showToolTip(tripMatch) && (
                    <Tooltip
                      title={`Total Trip Price: ${getTotalBudgetString(totalTripPrice.amount, 2, 2, totalTripPrice.currencyCode)}`}
                      placement="top-start"
                    >
                      <OutlinedInformationCircleIcon
                        color="action"
                        sx={{ fontSize: 16, ml: 0.5 }}
                      />
                    </Tooltip>
                    )}
                  </Box>
                )}
              />
              <IconLabel
                icon={OutlinedUserIcon}
                label={showAssignedSpecialist ? (assignedToName || 'Unassigned') : partnerName}
              />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                <TripMatchStatusChip
                  status={tripMatch.status}
                />
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={1.5}>
                  <PhoneIndicator
                    primaryTravelerPhone={travelers[0].phone}
                  />
                  <ChatIndicator
                    assignedTo={tripMatch.assignedTo}
                    createdAt={createdAt}
                    isAgentContext
                    tripMatchStatus={tripMatch.status}
                    tripMessageState={tripMatch.tripMessageState}
                  />
                  <SalesStageIndicator
                    salesStage={tripMatch.salesStage}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1}>
            <Grid item xs={2.5}>
              <Stack direction="row" spacing={1}>
                <Typography variant="bodyBold">
                  {fullName}
                </Typography>
                <TravelerChips
                  travelers={travelers}
                  referralType={tripMatch.referralType}
                  repeatTraveler={tripMatch.repeatTraveler}
                />
              </Stack>
              <Typography variant="caption">
                {countryNames}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {departureDate}
              </Typography>
              <Typography variant="caption">
                {totalNights}
                {' '}
                ·
                {' '}
                {totalTravelers}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {getPPPDString(pppd)}
              </Typography>
              <Box sx={{
                display: 'flex',
              }}
              >
                <Typography variant="caption">
                  {`${getTotalBudgetString(budget)} Total Budget`}
                </Typography>
                {showToolTip(tripMatch) && (
                  <Tooltip
                    title={`Total Trip Price: ${getTotalBudgetString(totalTripPrice.amount, 2, 2, totalTripPrice.currencyCode)}`}
                    placement="top-start"
                    color="primary"
                  >
                    <span>
                      <OutlinedInformationCircleIcon
                        color="action"
                        sx={{
                          ml: 0.5,
                          fontSize: 16,
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {showAssignedSpecialist ? (assignedToName || 'Unassigned') : partnerName}
              </Typography>
              <Typography variant="caption">
                Matched:
                {' '}
                {matchedDate}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Stack direction="row" spacing={3}>
                <PhoneIndicator
                  primaryTravelerPhone={travelers[0].phone}
                />
                <ChatIndicator
                  assignedTo={tripMatch.assignedTo}
                  createdAt={createdAt}
                  isAgentContext
                  tripMatchStatus={tripMatch.status}
                  tripMessageState={tripMatch.tripMessageState}
                />
                <SalesStageIndicator
                  salesStage={tripMatch.salesStage}
                />
              </Stack>

            </Grid>
            <Grid item xs={1.5} display="flex" justifyContent="flex-end">
              <TripMatchStatusChip
                status={tripMatch.status}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AllCardView;
